import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { PasswordInput } from '../components/common/PasswordInput';
import { 
  ArrowLeft, 
  Lock, 
  ArrowRight,
  Sparkle,
  BookOpen,
  WarningCircle,
  ShieldCheck,
  Heartbeat,
  Stethoscope,
  Globe,
  CaretRight,
  GraduationCap,
  FileText,
  Download,
  Buildings
} from '@phosphor-icons/react';
import MyPerformanceCard from '../components/staff/MyPerformanceCard';
import MyScoringCard from '../components/staff/MyScoringCard';
import MyManagerObjectives from '../components/staff/MyManagerObjectives';
import PdfViewerDialog from '../components/PdfViewerDialog';

const ACCESS_CODE = 'SADA2024';

const usefulLinks = [
  { name: 'Vidal', url: 'https://www.vidal.fr', description: 'Base de données médicaments', color: 'bg-blue-600' },
  { name: 'AIRP CI', url: 'https://airp.ci', description: 'Autorité Ivoirienne', color: 'bg-emerald-600' },
  { name: 'CNOP CI', url: 'https://cnop.ci', description: 'Ordre des Pharmaciens', color: 'bg-purple-600' },
  { name: 'Ubipharm', url: 'https://client-planetpharma.ubipharm.com/', description: 'Grossiste', color: 'bg-orange-500' },
  { name: 'Copharmed', url: 'https://www.copharmed.com', description: 'Grossiste', color: 'bg-teal-600' },
  { name: 'Tedis', url: 'https://www.tedispharma-ci.com/', description: 'Grossiste', color: 'bg-red-500' },
  { name: 'DPCI', url: 'https://pro2.dpci.ci/login', description: 'Direction Pharmacie', color: 'bg-amber-600' },
];

const trainingDocs = [
  { 
    name: 'Manuel SADASkinCare', 
    url: '/docs/MANUEL_UTILISATEUR_SADASKINCARE.pdf', 
    description: 'Guide complet du module dermo-cosmétique',
    icon: 'skincare',
    color: 'bg-pink-500'
  },
  { 
    name: 'Aide-Mémoire SkinCare', 
    url: '/docs/AIDE_MEMOIRE_SADASKINCARE.pdf', 
    description: 'Fiche rapide SADASkinCare',
    icon: 'memo',
    color: 'bg-pink-400'
  },
  { 
    name: 'Manuel SADAHealth', 
    url: '/docs/MANUEL_UTILISATEUR_SADAHEALTH.pdf', 
    description: 'Guide complet du module santé bébé',
    icon: 'health',
    color: 'bg-blue-500'
  },
  { 
    name: 'Aide-Mémoire Health', 
    url: '/docs/AIDE_MEMOIRE_SADAHEALTH.pdf', 
    description: 'Fiche rapide SADAHealth',
    icon: 'memo',
    color: 'bg-blue-400'
  },
  { 
    name: 'Manuel SADARayons', 
    url: '/docs/MANUEL_UTILISATEUR_SADARAYONS.pdf', 
    description: "Guide complet du module d'inventaire tournant",
    icon: 'stock',
    color: 'bg-purple-600'
  },
  { 
    name: 'Aide-Mémoire SADARayons', 
    url: '/docs/AIDE_MEMOIRE_SADARAYONS.pdf', 
    description: 'Fiche rapide inventaire tournant',
    icon: 'memo',
    color: 'bg-purple-400'
  },
  { 
    name: 'Manuel SADAGAS', 
    url: '/docs/MANUEL_UTILISATEUR_SADAGAS.pdf', 
    description: 'Guide complet du module réception BL',
    icon: 'reception',
    color: 'bg-orange-600'
  },
  { 
    name: 'Aide-Mémoire SADAGAS', 
    url: '/docs/AIDE_MEMOIRE_SADAGAS.pdf', 
    description: 'Fiche rapide scan de Bons de Livraison',
    icon: 'memo',
    color: 'bg-orange-400'
  },
  { 
    name: 'Manuel Formateur', 
    url: '/docs/MANUEL_FORMATEUR.pdf', 
    description: 'Guide pour former les équipes',
    icon: 'trainer',
    color: 'bg-amber-500'
  },
  { 
    name: 'Installation Tablette', 
    url: '/docs/GUIDE_INSTALLATION_TABLETTE.pdf',
    description: 'Configurer la tablette comptoir (Galaxy Tab A9+)',
    icon: 'install',
    color: 'bg-emerald-600'
  },
  { 
    name: 'Installation Téléphone', 
    url: '/docs/GUIDE_INSTALLATION_TELEPHONE.pdf',
    description: 'Configurer un téléphone agent (Redmi Note 14)',
    icon: 'install',
    color: 'bg-emerald-500'
  },
  { 
    name: 'Champion Digital', 
    url: '/docs/GUIDE_CHAMPION_DIGITAL.pdf',
    description: 'Guide Léonce — tests, support, remontée bugs',
    icon: 'champion',
    color: 'bg-fuchsia-600'
  },
];

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: 'easeOut' } }
};

const StaffPortalPage = ({ onBack, onAccessSADASkinCare, onAccessSADASSUR, onAccessB2B }) => {
  const { language } = useLanguage();
  const { user } = useAuth();
  const [accessCode, setAccessCode] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [error, setError] = useState('');
  const [previewDoc, setPreviewDoc] = useState(null);
  const [branding, setBranding] = useState({
    image_url: '/images/sada_accueil.png',
    overlay_color: '#2E7D32',
    overlay_opacity: 80,
    title: 'Pharmacie SADA',
    subtitle: 'Plateforme professionnelle de conseil dermo-cosmétique et suivi santé',
  });

  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/api/settings/portal-branding`)
      .then((res) => setBranding((b) => ({ ...b, ...res.data })))
      .catch(() => {});
  }, []);

  const handleCodeSubmit = (e) => {
    e.preventDefault();
    if (accessCode.toUpperCase() === ACCESS_CODE) {
      setIsAuthenticated(true);
      setError('');
    } else {
      setError(language === 'fr' ? 'Code incorrect' : 'Incorrect code');
    }
  };

  // Login Page - Split Screen Design
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex">
        {/* Left Side - Login Form */}
        <div className="flex-1 flex flex-col bg-white">
          {/* Back Button */}
          <div className="p-6">
            <button
              onClick={onBack}
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors font-medium text-sm"
              data-testid="back-to-public-btn-code"
            >
              <ArrowLeft size={18} weight="bold" />
              Retour au site
            </button>
          </div>

          {/* Login Form - Centered */}
          <div className="flex-1 flex items-center justify-center px-6 pb-12">
            <motion.div 
              className="w-full max-w-sm"
              initial="hidden"
              animate="visible"
              variants={fadeInUp}
            >
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-[#E8F5E9] rounded-2xl flex items-center justify-center mx-auto mb-5">
                  <Lock size={32} weight="duotone" className="text-[#2E7D32]" />
                </div>
                <h1 className="font-display text-3xl font-bold text-gray-900 mb-2">
                  Espace Conseiller
                </h1>
                <p className="text-gray-500">
                  Entrez le code d'accès pharmacie
                </p>
              </div>

              <form onSubmit={handleCodeSubmit} className="space-y-5">
                <PasswordInput
                  placeholder="Code d'accès"
                  value={accessCode}
                  onChange={(e) => setAccessCode(e.target.value)}
                  className="text-center text-xl tracking-[0.3em] font-medium py-6 bg-gray-50 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#2E7D32]/20 focus:border-[#2E7D32]"
                  data-testid="access-code-input"
                />
                
                {error && (
                  <motion.div 
                    className="flex items-center justify-center gap-2 text-red-600 text-sm"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    <WarningCircle size={18} weight="fill" />
                    {error}
                  </motion.div>
                )}

                <Button
                  type="submit"
                  className="w-full bg-[#2E7D32] hover:bg-[#1B5E20] text-white py-6 rounded-xl font-medium text-base transition-colors"
                  data-testid="submit-code-btn"
                >
                  Accéder
                </Button>
              </form>
            </motion.div>
          </div>
        </div>

        {/* Right Side - Branding with Background Image (Hidden on mobile) */}
        <div className="hidden lg:flex lg:w-[45%] relative flex-col justify-center items-center p-12 overflow-hidden">
          {/* Background Image - configurable via Admin > Apparence */}
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: `url('${
                branding.image_url?.startsWith('http')
                  ? branding.image_url
                  : (branding.image_url?.startsWith('/uploads')
                      ? process.env.REACT_APP_BACKEND_URL + branding.image_url
                      : branding.image_url)
              }')`,
            }}
          />
          {/* Color Overlay */}
          <div
            className="absolute inset-0"
            style={{
              backgroundColor: branding.overlay_color,
              opacity: (branding.overlay_opacity ?? 80) / 100,
            }}
          />
          
          {/* Decorative Pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-20 left-10 w-40 h-40 border-2 border-white rounded-full" />
            <div className="absolute bottom-32 right-20 w-64 h-64 border-2 border-white rounded-full" />
            <div className="absolute top-1/2 left-1/3 w-24 h-24 border-2 border-white rounded-full" />
          </div>
          
          <div className="relative z-10 text-center text-white max-w-md">
            <div className="w-20 h-20 bg-white/20 backdrop-blur rounded-3xl flex items-center justify-center mx-auto mb-8">
              <Stethoscope size={40} weight="duotone" className="text-white" />
            </div>
            <h2 className="font-display text-4xl font-bold mb-4">
              {branding.title}
            </h2>
            <p className="text-white/80 text-lg leading-relaxed">
              {branding.subtitle}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Authenticated - Module Selection Page
  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      {/* Header */}
      <header className="bg-[#2E7D32]">
        <div className="max-w-7xl mx-auto px-4 py-5">
          <div className="flex items-center justify-between">
            <button
              onClick={onBack}
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white/20 hover:bg-white/30 text-white transition-colors font-medium text-sm"
              data-testid="back-to-public-btn"
            >
              <ArrowLeft size={18} weight="bold" />
              Retour au site
            </button>
            <h1 className="font-display text-xl font-semibold text-white">Espace Conseiller</h1>
            <div className="w-24" />
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-10">
        {/* Main App Access Card */}
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeInUp}
        >
          <Card 
            className="mb-6 border-2 border-[#2E7D32] bg-white shadow-card rounded-2xl cursor-pointer hover:shadow-card-hover hover:-translate-y-1 transition-all group"
            onClick={onAccessSADASkinCare}
            data-testid="espace-conseiller-apps-forward"
          >
            <CardContent className="p-6 md:p-8">
              <div className="flex items-center gap-5">
                <div className="w-16 h-16 bg-[#E8F5E9] rounded-2xl flex items-center justify-center flex-shrink-0">
                  <Sparkle size={32} weight="duotone" className="text-[#2E7D32]" />
                </div>
                <div className="flex-1">
                  <h2 className="font-display text-xl md:text-2xl font-bold text-gray-900 mb-1">
                    Applications Métier
                  </h2>
                  <p className="text-gray-500">
                    SADASkinCare • SADAHealth • SADARayons • SADAGAS
                  </p>
                </div>
                <div className="w-12 h-12 bg-[#E8F5E9] rounded-xl flex items-center justify-center group-hover:bg-[#2E7D32] transition-colors">
                  <CaretRight size={24} weight="bold" className="text-[#2E7D32] group-hover:text-white transition-colors" />
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* NOTE : la bannière B2B a été retirée d'ici à la demande du client.
            Le B2B doit être accessible UNIQUEMENT via Applications Métier
            (login JWT requis), pas via le simple code conseiller. */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="mb-6"
        >
          <MyPerformanceCard language={language} />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.17 }}
          className="mb-6"
        >
          {/* Carte objectifs commerciaux Manager — visible uniquement pour Cyrille */}
          <MyManagerObjectives />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.18 }}
          className="mb-6"
        >
          <MyScoringCard />
        </motion.div>

        {/* Useful Links Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <div className="flex items-center gap-2 mb-5">
            <BookOpen size={22} weight="duotone" className="text-[#2E7D32]" />
            <h2 className="font-display text-lg font-semibold text-gray-900">
              Liens Utiles
            </h2>
          </div>
          
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {usefulLinks.map((link, index) => (
              <motion.a
                key={link.name}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 + index * 0.05 }}
              >
                <Card className="bg-white border border-gray-100 shadow-card rounded-xl hover:shadow-card-hover hover:-translate-y-1 transition-all h-full">
                  <CardContent className="p-4 text-center">
                    <div className={`w-10 h-10 ${link.color} rounded-xl flex items-center justify-center mx-auto mb-3`}>
                      <Globe size={20} weight="bold" className="text-white" />
                    </div>
                    <p className="font-medium text-gray-900 text-sm group-hover:text-[#2E7D32] transition-colors">
                      {link.name}
                    </p>
                    <p className="text-xs text-gray-500 mt-1 line-clamp-1">
                      {link.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.a>
            ))}
          </div>
        </motion.div>

        {/* Training & Documentation Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mt-8"
        >
          <div className="flex items-center gap-2 mb-5">
            <GraduationCap size={22} weight="duotone" className="text-[#2E7D32]" />
            <h2 className="font-display text-lg font-semibold text-gray-900">
              Aide & Formation
            </h2>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {trainingDocs.map((doc, index) => (
              <motion.div
                key={doc.name}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 + index * 0.05 }}
              >
                <Card className="bg-white border border-gray-100 shadow-card rounded-xl hover:shadow-card-hover hover:-translate-y-1 transition-all h-full">
                  <CardContent className="p-5">
                    <button
                      type="button"
                      onClick={() => setPreviewDoc(doc)}
                      className="w-full text-left group focus:outline-none focus:ring-2 focus:ring-[#2E7D32] focus:ring-offset-2 rounded-lg"
                      data-testid={`doc-preview-${doc.url.split('/').pop()}`}
                      aria-label={`Aperçu — ${doc.name}`}
                    >
                      <div className="flex items-start gap-4">
                        <div className={`w-12 h-12 ${doc.color} rounded-xl flex items-center justify-center flex-shrink-0`}>
                          <FileText size={24} weight="bold" className="text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 text-sm group-hover:text-[#2E7D32] transition-colors">
                            {doc.name}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            {doc.description}
                          </p>
                          <p className="text-[10px] text-[#2E7D32] mt-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                            Cliquez pour consulter →
                          </p>
                        </div>
                      </div>
                    </button>
                    <div className="flex items-center justify-end mt-3 pt-3 border-t border-gray-100 gap-1">
                      <button
                        type="button"
                        onClick={() => setPreviewDoc(doc)}
                        className="text-[11px] font-medium text-[#2E7D32] hover:bg-[#E8F5E9] px-2 py-1 rounded transition-colors"
                        data-testid={`doc-view-${doc.url.split('/').pop()}`}
                      >
                        Aperçu
                      </button>
                      <a
                        href={doc.url}
                        download
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-[11px] font-medium text-gray-600 hover:bg-gray-100 px-2 py-1 rounded transition-colors"
                        data-testid={`doc-download-${doc.url.split('/').pop()}`}
                        aria-label={`Télécharger ${doc.name}`}
                      >
                        <Download size={12} weight="bold" />
                        Télécharger
                      </a>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
      <PdfViewerDialog
        open={!!previewDoc}
        onOpenChange={(open) => { if (!open) setPreviewDoc(null); }}
        url={previewDoc?.url}
        title={previewDoc?.name}
      />
    </div>
  );
};

export default StaffPortalPage;
